import { useState, useEffect, useMemo, lazy, Suspense, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdClose, MdExpandMore, MdExpandLess, MdVerified, MdLocationOn, MdWork, MdAccessTime, MdFavoriteBorder, MdChevronRight, MdTune } from 'react-icons/md';
import Footer from '../components/Footer';
import ResultsList from '../components/search/ResultsList';
import MapPanel from '../components/search/MapPanel';
import FiltersDrawer, { DEFAULT_FILTERS } from '../components/search/FiltersDrawer';
import ResultCard from '../components/search/ResultCard';
import ResultsToolbar from '../components/search/ResultsToolbar';
import AuthModal from '../components/auth/AuthModal';
import LandingHeader from '../components/landing/LandingHeader';
import HeroSection from '../components/landing/HeroSection';
import HowItWorks from '../components/landing/HowItWorks';
import BenefitsSection from '../components/landing/BenefitsSection';
import FeaturesSection from '../components/landing/FeaturesSection';
import TestimonialsSection from '../components/landing/TestimonialsSection';
import api from '../services/api';
import { getUser } from '../services/auth';
import { t, getLanguage } from '../utils/i18n';

// Helper function to get avatar image from RandomUser.me API (free, no API key needed)
// Uses seed based on user ID to get consistent images
const getAvatarImage = (userId, firstName, lastName) => {
  if (!userId) return null;
  // Use user ID as seed for consistent image per user
  const seed = userId.toString().replace(/[^a-zA-Z0-9]/g, '').slice(0, 10) || 'default';
  const name = `${firstName || ''} ${lastName || ''}`.trim() || 'User';
  // RandomUser.me API - returns random but consistent image based on seed
  return `https://api.dicebear.com/7.x/avataaars/svg?seed=${seed}&backgroundColor=b6e3ff,c0aede,d1d4f9,ffd5dc,ffdfbf`;
};

export default function LandingPage({ initialAuthMode = null }) {
  const navigate = useNavigate();
  const user = getUser();
  const [, setLang] = useState(getLanguage());
  const [openFAQ, setOpenFAQ] = useState(null);
  const [searchMode, setSearchMode] = useState('parent'); // 'parent' or 'sitter'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [viewMode, setViewMode] = useState('map'); // 'map' or 'list' for results
  // Raw results loaded from API (used as "catalog" for map + list pagination)
  const [searchResults, setSearchResults] = useState([]);
  const [loadingResults, setLoadingResults] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchPage, setSearchPage] = useState(1);
  const [hasMoreResults, setHasMoreResults] = useState(false);
  const [isNearbySearch, setIsNearbySearch] = useState(false); // Flag za pretragu u blizini (2km)
  const [hoveredItemId, setHoveredItemId] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [selectedPinItemIds, setSelectedPinItemIds] = useState(null); // null = svi; array = filtrirano po pin-u
  const [mapBounds, setMapBounds] = useState(null);
  const lastMapBoundsRef = useRef(null);
  const [appliedListBounds, setAppliedListBounds] = useState(null);
  // Always ON: list follows current map bounds (no toggle)
  const [searchAsIMove] = useState(true);
  const [searchError, setSearchError] = useState(null);
  const [sortBy, setSortBy] = useState('default');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [resultAvatars, setResultAvatars] = useState({});
  const lastSearchRef = useRef(null);

  const locationAutocompleteRef = useRef(null);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    company: '', // honeypot
  });
  const [contactStatus, setContactStatus] = useState({ state: 'idle', message: '' }); // idle|sending|success|error
  const [showRegisterPrompt, setShowRegisterPrompt] = useState(false);
  const [registerPromptTarget, setRegisterPromptTarget] = useState(null); // path to go after login/register
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('login');

  const openAuthModal = (mode = 'login', redirect = null) => {
    setRegisterPromptTarget(redirect);
    setAuthModalMode(mode);
    setAuthModalOpen(true);
  };

  useEffect(() => {
    if (!initialAuthMode) return;
    setAuthModalMode(initialAuthMode);
    setAuthModalOpen(true);
  }, [initialAuthMode]);

  // Ulogovan korisnik na "/" — odmah na dashboard
  useEffect(() => {
    if (initialAuthMode) return; // ne diraj /login i /register rute
    if (user) {
      navigate('/dashboard', { replace: true });
    }
  }, [initialAuthMode, user, navigate]);

  useEffect(() => {
    const handleLanguageChange = () => {
      setLang(getLanguage());
    };
    
    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  // Bbox oko tačke (radius u km) — za API north/south/east/west
  const getBboxFromCenter = (lat, lon, radiusKm = 2) => {
    const latDelta = radiusKm / 111.0;
    const lonDelta = radiusKm / (111.0 * Math.cos((lat * Math.PI) / 180));
    return {
      south: lat - latDelta,
      north: lat + latDelta,
      west: lon - lonDelta,
      east: lon + lonDelta,
    };
  };

  // Izračunaj udaljenost između dve tačke (Haversine formula, rezultat u km)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Radius Zemlje u km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const handleLocationSelect = (location) => {
    const isNearby = location.type === 'nearby';
    setIsNearbySearch(isNearby);
    setSelectedLocation(location);
    setSearchQuery(location.label);
    // Mapa će se prikazati automatski kada selectedLocation postane setovan
    // Fetch search results for this location
    fetchSearchResults(location, isNearby);
  };

  // Normalizuj grad za API. Manje naselje / opština MORA pre širokog „Beograda“ — OSM često vrati grad=Beograd za Lazarevac itd.
  const normalizeCityForApi = (cityOrLabel) => {
    if (!cityOrLabel) return undefined;
    const s = String(cityOrLabel).toLowerCase();
    if (/lazarevac|лазаревац/i.test(s)) return 'Lazarevac';
    if (/lajkovac|лајковац/i.test(s)) return 'Lajkovac';
    if (/novi\s*sad|нови\s*сад/i.test(s)) return 'Novi Sad';
    if (/niš|nis|ниш/i.test(s)) return 'Niš';
    if (/pančevo|pancevo|панчево/i.test(s)) return 'Pančevo';
    const isBelgrade =
      /belgrade|^beograd\b|београд\b|stari\s*grad|стари\s*град|градска\s*општина|општина\s*стари/i.test(s) ||
      s.includes('beograd') ||
      s.includes('београд');
    if (isBelgrade) return 'Beograd';
    return cityOrLabel === 'Belgrade' ? 'Beograd' : cityOrLabel;
  };

  const PAGE_SIZE = 24;
  /** Koliko oglasa povući za mapu (mora biti u skladu sa backend GET /jobs/public max limit). */
  const JOBS_MAP_CATALOG_LIMIT = 1500;

  const fetchSearchResults = async (location, nearbyMode = false, page = 1, append = false) => {
    if (page === 1) {
      setLoadingResults(true);
      setSearchError(null);
    } else setLoadingMore(true);
    try {
      let allResults = [];

      if (searchMode === 'parent') {
        const cityForApi = location.city || location.label?.split(',')[0]?.trim();
        const cityParam = nearbyMode ? undefined : normalizeCityForApi(cityForApi) || undefined;
        // Za mapu moramo imati "sve" rezultate (u razumnom limitu) da bi pinovi uvek bili kompletni u trenutnom view-u.
        // Lista levo ostaje paginirana preko PAGE_SIZE (slice), bez dodatnog API poziva.
        const limit = 500;
        const response = await api.get('/users/providers', {
          params: { city: cityParam, limit, page: 1 }
        });
        allResults = response.data?.providers || [];
      } else {
        // Jobs (sitter mode): bez „uvek bbox oko tačke” — to je na gustom Beogradu ispunjavalo svih 500 rezultata
        // samo BG pinovima i skrivalo NS, Pančevo, Lazarevac itd. Bbox samo za pravu pretragu „u blizini”.
        // Inače: opcioni filter po gradu (iz autocomplete-a / naslova); ako nema grada — nacionalno (mešavina).
        const params = { limit: JOBS_MAP_CATALOG_LIMIT, page };
        if (nearbyMode && location?.lat != null && location?.lon != null) {
          const bbox = getBboxFromCenter(Number(location.lat), Number(location.lon), 5);
          params.north = bbox.north;
          params.south = bbox.south;
          params.east = bbox.east;
          params.west = bbox.west;
        } else {
          const rawCity = location?.city || location?.label?.split(',')[0]?.trim();
          const cityForApi = normalizeCityForApi(rawCity);
          if (cityForApi) params.city = cityForApi;
        }
        const response = await api.get('/jobs/public', { params }).catch(() => ({ data: { jobs: [] } }));
        allResults = response.data?.jobs || [];
        const pg = response.data?.pagination;
        if (pg?.pages != null && pg?.current != null) {
          setHasMoreResults(Number(pg.current) < Number(pg.pages));
        } else {
          setHasMoreResults(allResults.length >= JOBS_MAP_CATALOG_LIMIT);
        }
      }

      // Za parent mode nearby: filtriraj po 2km; za jobs bbox već ograničava na API-ju
      let list = allResults;
      if (searchMode === 'parent' && nearbyMode && location.lat && location.lon) {
        list = allResults.filter(item => {
          const itemLat = item.address?.lat || item.address?.latitude;
          const itemLon = item.address?.lng || item.address?.longitude;
          if (!itemLat || !itemLon) return false;
          const distance = calculateDistance(location.lat, location.lon, parseFloat(itemLat), parseFloat(itemLon));
          return distance <= 2.0;
        });
        setHasMoreResults(false);
      }

      if (searchMode === 'parent') {
        // One-shot catalog for map + list pagination
        setSearchResults(list);
        setSearchPage(1);
        setSelectedPinItemIds(null);
        setMapBounds(null); // nova pretraga — prikaži sve dok mapa ne pošalje bounds
        lastSearchRef.current = { location, nearbyMode };
      } else {
        // Jobs: server paging as before
        if (append) {
          setSearchResults(prev => [...prev, ...list]);
          setSearchPage(page);
        } else {
          setSearchResults(list);
          setSearchPage(1);
          setSelectedPinItemIds(null);
          setMapBounds(null);
          lastSearchRef.current = { location, nearbyMode };
        }
      }
    } catch (error) {
      console.error('Error fetching search results:', error);
      setSearchError(error?.message || 'Greška pri učitavanju');
      if (!append) setSearchResults([]);
    } finally {
      setLoadingResults(false);
      setLoadingMore(false);
    }
  };

  const loadMoreResults = () => {
    const last = lastSearchRef.current;
    if (!last || loadingMore || !hasMoreResults) return;
    if (searchMode === 'parent') {
      // Local paging over catalog (no extra API call)
      setSearchPage((p) => p + 1);
      return;
    }
    fetchSearchResults(last.location, last.nearbyMode, searchPage + 1, true);
  };

  // Rezultati u vidljivoj oblasti mape (zoom/pan osvežava); kad nema bounds prikaži sve
  const getItemLatLng = (item) => {
    const addr = searchMode === 'parent' ? item.address : item.location;
    const lat = addr?.lat != null ? parseFloat(addr.lat) : null;
    const lng = (addr?.lng != null ? parseFloat(addr.lng) : addr?.lon != null ? parseFloat(addr.lon) : null);
    return { lat, lng };
  };
  // Filtered + sorted catalog (shared for map + list)
  const baseResults = useMemo(() => {
    let list = searchResults;
    // Client-side filters
    if (filters.priceMin !== '' || filters.priceMax !== '') {
      const min = filters.priceMin !== '' ? Number(filters.priceMin) : 0;
      const max = filters.priceMax !== '' ? Number(filters.priceMax) : Infinity;
      list = list.filter((item) => {
        const rate = item.hourlyRate ?? 0;
        return rate >= min && rate <= max;
      });
    }
    if (filters.ratingMin !== '') {
      const minR = Number(filters.ratingMin);
      list = list.filter((item) => (item.rating?.average ?? 0) >= minR);
    }
    if (filters.verifiedOnly) {
      list = list.filter((item) => !!item.verified);
    }
    if (sortBy === 'price_asc') list = [...list].sort((a, b) => (a.hourlyRate ?? 0) - (b.hourlyRate ?? 0));
    else if (sortBy === 'price_desc') list = [...list].sort((a, b) => (b.hourlyRate ?? 0) - (a.hourlyRate ?? 0));
    else if (sortBy === 'rating') list = [...list].sort((a, b) => (b.rating?.average ?? 0) - (a.rating?.average ?? 0));
    return list;
  }, [searchResults, sortBy, filters]);

  const selectedItem = useMemo(() => {
    if (!selectedItemId) return null;
    return baseResults.find((x) => x?.id === selectedItemId) || null;
  }, [baseResults, selectedItemId]);

  // LIST filtering behavior:
  // - If searchAsIMove ON: list follows current map bounds
  // - Else: list follows "applied" bounds only after clicking CTA (Pretraži ovu oblast)
  // - If selectedPinItemIds: filter to only those items (pin/cluster click)
  const listBase = useMemo(() => {
    const bounds = searchAsIMove ? mapBounds : appliedListBounds;
    let base = baseResults;
    if (bounds) {
      const { north, south, east, west } = bounds;
      base = base.filter((item) => {
        const { lat, lng } = getItemLatLng(item);
        if (lat == null || lng == null) return false;
        return lat >= south && lat <= north && lng >= west && lng <= east;
      });
    }
    if (selectedPinItemIds && selectedPinItemIds.length > 0) {
      const idSet = new Set(selectedPinItemIds);
      base = base.filter((item) => idSet.has(item.id));
    }
    return base;
  }, [baseResults, searchAsIMove, mapBounds, appliedListBounds, selectedPinItemIds]);

  // LIST: paginated (load more) over catalog
  const listResults = useMemo(() => {
    if (searchMode !== 'parent') return listBase;
    return listBase.slice(0, searchPage * PAGE_SIZE);
  }, [listBase, searchMode, searchPage]);

  // MAP: always show pins for the *currently visible* map bounds
  const mapResults = useMemo(() => {
    if (!mapBounds || baseResults.length === 0) return baseResults;
    const { north, south, east, west } = mapBounds;
    return baseResults.filter((item) => {
      const { lat, lng } = getItemLatLng(item);
      if (lat == null || lng == null) return false;
      return lat >= south && lat <= north && lng >= west && lng <= east;
    });
  }, [baseResults, mapBounds]);

  // hasMore depends on catalog size (parent) or server paging (jobs)
  useEffect(() => {
    if (searchMode !== 'parent') return;
    setHasMoreResults(listResults.length < listBase.length);
  }, [searchMode, listResults.length, listBase.length]);

  const handleSearch = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!searchQuery.trim()) return;
    
    locationAutocompleteRef.current?.closeDropdown();
    
    // Ne navigirati na /dashboard/jobs — uvek ostajemo na landingu i prikazujemo rezultate ovde.
    let locationToUse = selectedLocation;
    
    if (!locationToUse || !locationToUse.lat) {
      // Ako nije izabrano iz dropdown-a (Nominatim može biti blokiran), radi samo city-based search bez geokodiranja
      locationToUse = { label: searchQuery.trim() };
      setSelectedLocation(locationToUse);
    }
    
    await fetchSearchResults(locationToUse);
  };

  const submitContact = async (e) => {
    e.preventDefault();
    if (contactStatus.state === 'sending') return;

    setContactStatus({ state: 'sending', message: '' });
    try {
      await api.post('/contact', {
        name: contactForm.name,
        email: contactForm.email,
        subject: contactForm.subject,
        message: contactForm.message,
        company: contactForm.company,
      });
      setContactStatus({ state: 'success', message: 'Poruka je poslata. Javićemo se uskoro.' });
      setContactForm({ name: '', email: '', subject: '', message: '', company: '' });
    } catch (err) {
      setContactStatus({
        state: 'error',
        message: err?.response?.data?.message || 'Greška pri slanju poruke. Pokušajte ponovo.',
      });
    }
  };

  // Avatari stvarnih ljudi za rezultate pretrage (kad nema photoUrl) — randomuser.me po id-u
  useEffect(() => {
    if (searchResults.length === 0) return;
    const needAvatar = searchResults.filter((item) => !(item.photoUrl || item.photo_url || item.profileImage));
    if (needAvatar.length === 0) return;
    const CACHE_KEY = 'landing_result_avatars_v1';
    const cache = (() => {
      try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (!raw) return {};
        const { data, savedAt } = JSON.parse(raw);
        if (Date.now() - savedAt > 14 * 24 * 60 * 60 * 1000) return {};
        return data || {};
      } catch { return {}; }
    })();
    const toFetch = needAvatar.filter((item) => !cache[item.id]);
    if (toFetch.length === 0) {
      setResultAvatars((prev) => ({ ...prev, ...cache }));
      return;
    }
    const controller = new AbortController();
    (async () => {
      const next = { ...cache };
      for (const item of toFetch.slice(0, 30)) {
        try {
          const r = await fetch(
            `https://randomuser.me/api/?seed=${item.id}&results=1&inc=picture&gender=female`,
            { signal: controller.signal }
          );
          if (!r.ok) continue;
          const j = await r.json();
          const pic = j?.results?.[0]?.picture?.medium;
          if (pic) next[item.id] = pic;
        } catch {
          // ignore
        }
      }
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ data: next, savedAt: Date.now() }));
      } catch {}
      setResultAvatars((prev) => ({ ...prev, ...next }));
    })();
    return () => controller.abort();
  }, [searchResults.map((i) => i.id).join(',')]);

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden landing-page-bg">
      <div className="z-10 relative">
        <LandingHeader onOpenAuth={openAuthModal} />

        <HeroSection
          searchMode={searchMode}
          onSearchModeChange={setSearchMode}
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          onLocationSelect={handleLocationSelect}
          onSearch={handleSearch}
          locationAutocompleteRef={locationAutocompleteRef}
        >
          {((selectedLocation && selectedLocation.lat && selectedLocation.lon) || searchResults.length > 0 || loadingResults) && (
            <div className="w-full animate-fade-in">
              <div className="max-w-[1600px] mx-auto">
                <ResultsToolbar
                  count={listResults.length}
                  totalCount={baseResults.length}
                  locationLabel={searchQuery || undefined}
                  sortBy={sortBy}
                  onSortChange={setSortBy}
                  onOpenFilters={() => setShowFilters((v) => !v)}
                  filtersOpen={showFilters}
                  viewMode={viewMode}
                  onViewModeChange={(next) => {
                    if (next === 'list') setSelectedItemId(null);
                    setViewMode(next);
                  }}
                  onClear={() => {
                    setSelectedLocation(null);
                    setSearchQuery('');
                    setSearchResults([]);
                    setSelectedItemId(null);
                    setSelectedPinItemIds(null);
                    setShowFilters(false);
                  }}
                />

                {/* Filters drawer: mobile = full-screen, desktop = inline panel below bar when open */}
                <div className="mb-4">
                  <div className="lg:hidden">
                    <FiltersDrawer
                      open={showFilters}
                      onClose={() => setShowFilters(false)}
                      filters={filters}
                      onApply={setFilters}
                      onReset={() => setFilters(DEFAULT_FILTERS)}
                      isMobile={true}
                    />
                  </div>
                  <div className="hidden lg:block">
                    {showFilters && (
                      <FiltersDrawer
                        open={showFilters}
                        onClose={() => setShowFilters(false)}
                        filters={filters}
                        onApply={setFilters}
                        onReset={() => setFilters(DEFAULT_FILTERS)}
                        isMobile={false}
                      />
                    )}
                  </div>
                </div>

                {/* Desktop: 50/50 lista i mapa. Mobile: bira se Mapa ili Lista */}
                <div className="relative grid grid-cols-1 lg:grid-cols-[minmax(420px,42%)_minmax(0,58%)] gap-4 min-h-0">
                  {/* Lista */}
                  <div
                    className={`bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-4 lg:p-5 max-h-[calc(100vh-12rem)] min-h-[520px] overflow-y-auto min-w-0 ${
                      viewMode === 'map' ? 'hidden lg:block' : 'block'
                    }`}
                  >
                    {/* Mobile: dugme Nazad na mapu kada je prikaz Liste */}
                    {viewMode === 'list' && (
                      <div className="lg:hidden mb-3">
                        <button
                          type="button"
                          onClick={() => setViewMode('map')}
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                        >
                          ← Nazad na mapu
                        </button>
                      </div>
                    )}
                    {selectedPinItemIds && selectedPinItemIds.length > 0 && (
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Prikazano {listBase.length} od pin-a na mapi
                        </span>
                        <button
                          type="button"
                          onClick={() => setSelectedPinItemIds(null)}
                          className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                        >
                          Prikaži sve
                        </button>
                      </div>
                    )}
                    <ResultsList
                      items={listResults}
                      isParent={searchMode === 'parent'}
                      getAvatarUrl={(item) => item.photoUrl || item.photo_url || item.profileImage || resultAvatars[item.id] || (searchMode === 'parent' ? getAvatarImage(item.id, item.firstName, item.lastName) : null)}
                      selectedItemId={selectedItemId}
                      hoveredItemId={hoveredItemId}
                      scrollToHovered={false}
                      onSelectItem={(item) => {
                        const next = selectedItemId === item.id ? null : item.id;
                        setSelectedItemId(next);
                        if (next && searchMode === 'parent') {
                          navigate(`/babysitter/${item.id}`);
                        } else if (next && searchMode !== 'parent') {
                          window.open(`/job/${item.id}`, '_blank', 'noopener,noreferrer');
                        }
                      }}
                      onContactClick={searchMode === 'parent' ? (item) => {
                        if (user) {
                          navigate(`/dashboard/conversations?userId=${item.id}`);
                        } else {
                          const target = `/dashboard/conversations?userId=${item.id}`;
                          setRegisterPromptTarget(target);
                          setShowRegisterPrompt(true);
                        }
                      } : undefined}
                      onHoverItem={setHoveredItemId}
                      loading={loadingResults}
                      error={searchError}
                      onRetry={() => lastSearchRef.current && fetchSearchResults(lastSearchRef.current.location, lastSearchRef.current.nearbyMode)}
                      hasMoreResults={hasMoreResults}
                      loadingMore={loadingMore}
                      onLoadMore={loadMoreResults}
                      emptyInBounds={false}
                      emptyVariant={listResults.length === 0 && (selectedLocation?.lat != null || lastSearchRef.current?.location?.lat != null) ? 'noResultsInArea' : 'default'}
                      emptyVariantProps={{
                        isParent: searchMode === 'parent',
                        onClear: () => {
                          setSelectedLocation(null);
                          setSearchQuery('');
                          setSearchResults([]);
                          setSelectedItemId(null);
                          setSelectedPinItemIds(null);
                          setShowFilters(false);
                        },
                      }}
                      listHeight={520}
                    />
                  </div>

                  {/* Mapa */}
                  <div
                    className={`rounded-2xl overflow-hidden h-[400px] min-h-[360px] lg:h-[calc(100vh-12rem)] lg:min-h-[520px] lg:min-h-0 w-full block ${
                      viewMode === 'list' ? 'hidden lg:block' : 'block'
                    }`}
                  >
                    <MapPanel
                      key={selectedLocation?.lat != null ? `${selectedLocation.lat}-${selectedLocation.lon}` : 'no-location'}
                      className="h-full min-h-0"
                      showMapFooter={false}
                      isVisible={viewMode === 'map'}
                      markerHoverTooltipOnly
                      items={mapResults}
                      isParent={searchMode === 'parent'}
                      userAddress={selectedLocation?.lat != null && selectedLocation?.lon != null ? { lat: selectedLocation.lat, lng: selectedLocation.lon, city: selectedLocation.city || selectedLocation.label?.split(',')[0], label: selectedLocation.label, nearby: isNearbySearch } : null}
                      friends={[]}
                      selectedItemId={selectedItemId}
                      hoveredItemId={hoveredItemId}
                      onMapMove={(bounds) => {
                        const prev = lastMapBoundsRef.current;
                        const eps = 1e-6;
                        const same =
                          prev &&
                          Math.abs(prev.north - bounds.north) < eps &&
                          Math.abs(prev.south - bounds.south) < eps &&
                          Math.abs(prev.east - bounds.east) < eps &&
                          Math.abs(prev.west - bounds.west) < eps;
                        if (!same) {
                          lastMapBoundsRef.current = bounds;
                          setMapBounds(bounds);
                        }
                      }}
                      showSearchThisArea={false}
                      onSearchThisArea={() => {
                        // Apply current map bounds to LIST (one-shot) and reset pagination
                        setAppliedListBounds(mapBounds);
                        setSearchPage(1);
                      }}
                      onMarkerClick={(itemOrItems) => {
                        const items = Array.isArray(itemOrItems) ? itemOrItems : [itemOrItems];
                        const ids = items.map((i) => i.id).filter(Boolean);
                        if (ids.length > 0) {
                          setSelectedPinItemIds(ids);
                          setViewMode('list');
                        }
                      }}
                      onMarkerHover={setHoveredItemId}
                      searchAsIMove={true}
                      getItemHref={(item) => (searchMode === 'parent' ? `/babysitter/${item.id}` : `/job/${item.id}`)}
                      onRecenter={() => {}}
                    />
                  </div>
                </div>

                {/* Mobile: pin preview (bottom) */}
                {viewMode === 'map' && selectedItem && (
                  <div className="lg:hidden fixed left-0 right-0 bottom-0 z-[70] px-4 pb-4">
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl p-3">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">Pregled</span>
                        <button
                          type="button"
                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
                          onClick={() => setSelectedItemId(null)}
                          aria-label="Zatvori pregled"
                        >
                          <MdClose className="text-lg" />
                        </button>
                      </div>
                      <ResultCard
                        item={selectedItem}
                        isParent={searchMode === 'parent'}
                        avatarUrl={selectedItem.photoUrl || selectedItem.photo_url || selectedItem.profileImage || resultAvatars[selectedItem.id]}
                        initials={(searchMode === 'parent' ? `${selectedItem.firstName?.[0] || ''}${selectedItem.lastName?.[0] || ''}`.toUpperCase() : 'P')}
                        isSelected={true}
                        isHovered={false}
                        onClick={() => {
                          if (searchMode === 'parent') {
                            navigate(`/babysitter/${selectedItem.id}`);
                          } else {
                            window.open(`/job/${selectedItem.id}`, '_blank', 'noopener,noreferrer');
                          }
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </HeroSection>

      <HowItWorks />

      <FeaturesSection />

      <BenefitsSection
        onOpenAuth={openAuthModal}
        onFindSitter={() => {
          setSearchMode('parent');
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      />

      <TestimonialsSection />

      {/* Pricing Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-b from-transparent via-white/30 to-white dark:via-gray-900/30 dark:to-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-4 sm:mb-6 text-gray-900 dark:text-gray-100">
            {t('landing.pricing.title')}
          </h2>
          <p className="text-center text-gray-700 dark:text-gray-300 mb-8 sm:mb-12 max-w-2xl mx-auto">
            {t('landing.pricing.description')}
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Free Plan */}
          <div className="card text-center border-2 border-gray-200 dark:border-gray-700">
            <h3 className="text-xl sm:text-2xl font-bold mb-2 text-gray-900 dark:text-gray-100">{t('landing.pricing.free')}</h3>
            <div className="mb-4">
              <span className="text-3xl sm:text-4xl font-bold text-primary-600 dark:text-primary-400">0</span>
              <span className="text-gray-700 dark:text-gray-300"> {t('landing.pricing.month')}</span>
            </div>
            <ul className="space-y-2 mb-6 text-sm sm:text-base flex flex-col items-center sm:items-stretch max-w-[17rem] sm:max-w-none mx-auto sm:mx-0 text-center sm:text-left">
              <li className="flex items-center sm:items-start justify-center sm:justify-start gap-2 w-full">
                <span className="text-green-500 dark:text-green-400 flex-shrink-0">✓</span>
                <span className="text-gray-900 dark:text-gray-200">Osnovno praćenje sati</span>
              </li>
              <li className="flex items-center sm:items-start justify-center sm:justify-start gap-2 w-full">
                <span className="text-green-500 dark:text-green-400 flex-shrink-0">✓</span>
                <span className="text-gray-900 dark:text-gray-200">Pregled kalendara</span>
              </li>
              <li className="flex items-center sm:items-start justify-center sm:justify-start gap-2 w-full">
                <span className="text-green-500 dark:text-green-400 flex-shrink-0">✓</span>
                <span className="text-gray-900 dark:text-gray-200">Jedan aktivan posao</span>
              </li>
              <li className="flex items-center sm:items-start justify-center sm:justify-start gap-2 w-full">
                <span className="text-gray-400 dark:text-gray-500 flex-shrink-0">✗</span>
                <span className="text-gray-600 dark:text-gray-400">Neograničeni poslovi</span>
              </li>
              <li className="flex items-center sm:items-start justify-center sm:justify-start gap-2 w-full">
                <span className="text-gray-400 dark:text-gray-500 flex-shrink-0">✗</span>
                <span className="text-gray-600 dark:text-gray-400">Napredni izveštaji</span>
              </li>
            </ul>
            <button type="button" onClick={() => openAuthModal('register')} className="btn-secondary w-full">
              Registruj se
            </button>
          </div>

          {/* Basic Plan */}
          <div className="card text-center border-2 border-primary-500 dark:border-primary-400 transform scale-105 relative">
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-primary-600 text-white px-4 py-1 rounded-full text-xs sm:text-sm font-semibold">
              {t('landing.pricing.mostPopular')}
            </div>
            <h3 className="text-xl sm:text-2xl font-bold mb-2 text-gray-900 dark:text-gray-100">{t('landing.pricing.basic')}</h3>
            <div className="mb-4">
              <span className="text-3xl sm:text-4xl font-bold text-primary-600 dark:text-primary-400">599</span>
              <span className="text-gray-700 dark:text-gray-300"> {t('landing.pricing.month')}</span>
            </div>
            <ul className="space-y-2 mb-6 text-sm sm:text-base flex flex-col items-center sm:items-stretch max-w-[17rem] sm:max-w-none mx-auto sm:mx-0 text-center sm:text-left">
              <li className="flex items-center sm:items-start justify-center sm:justify-start gap-2 w-full">
                <span className="text-green-500 dark:text-green-400 flex-shrink-0">✓</span>
                <span className="text-gray-900 dark:text-gray-200">Sve iz besplatnog plana</span>
              </li>
              <li className="flex items-center sm:items-start justify-center sm:justify-start gap-2 w-full">
                <span className="text-green-500 dark:text-green-400 flex-shrink-0">✓</span>
                <span className="text-gray-900 dark:text-gray-200">Neograničeni poslovi</span>
              </li>
              <li className="flex items-center sm:items-start justify-center sm:justify-start gap-2 w-full">
                <span className="text-green-500 dark:text-green-400 flex-shrink-0">✓</span>
                <span className="text-gray-900 dark:text-gray-200">Prioritet podrška</span>
              </li>
              <li className="flex items-center sm:items-start justify-center sm:justify-start gap-2 w-full">
                <span className="text-green-500 dark:text-green-400 flex-shrink-0">✓</span>
                <span className="text-gray-900 dark:text-gray-200">Izveštaji i statistike</span>
              </li>
              <li className="flex items-center sm:items-start justify-center sm:justify-start gap-2 w-full">
                <span className="text-gray-400 dark:text-gray-500 flex-shrink-0">✗</span>
                <span className="text-gray-600 dark:text-gray-400">Napredne analitike</span>
              </li>
            </ul>
            <button type="button" onClick={() => openAuthModal('register')} className="btn-primary w-full">
              Registruj se
            </button>
          </div>

          {/* Premium Plan */}
          <div className="card text-center border-2 border-primary-300 dark:border-primary-600 sm:col-span-2 lg:col-span-1">
            <h3 className="text-xl sm:text-2xl font-bold mb-2 text-gray-900 dark:text-gray-100">{t('landing.pricing.premium')}</h3>
            <div className="mb-4">
              <span className="text-3xl sm:text-4xl font-bold text-primary-600 dark:text-primary-400">899</span>
              <span className="text-gray-700 dark:text-gray-300"> {t('landing.pricing.month')}</span>
            </div>
            <ul className="space-y-2 mb-6 text-sm sm:text-base flex flex-col items-center sm:items-stretch max-w-[17rem] sm:max-w-none mx-auto sm:mx-0 text-center sm:text-left">
              <li className="flex items-center sm:items-start justify-center sm:justify-start gap-2 w-full">
                <span className="text-green-500 dark:text-green-400 flex-shrink-0">✓</span>
                <span className="text-gray-900 dark:text-gray-200">Sve iz Osnovnog plana</span>
              </li>
              <li className="flex items-center sm:items-start justify-center sm:justify-start gap-2 w-full">
                <span className="text-green-500 dark:text-green-400 flex-shrink-0">✓</span>
                <span className="text-gray-900 dark:text-gray-200">Napredne analitike i izveštaji</span>
              </li>
              <li className="flex items-center sm:items-start justify-center sm:justify-start gap-2 w-full">
                <span className="text-green-500 dark:text-green-400 flex-shrink-0">✓</span>
                <span className="text-gray-900 dark:text-gray-200">Prioritet na poslovima</span>
              </li>
              <li className="flex items-center sm:items-start justify-center sm:justify-start gap-2 w-full">
                <span className="text-green-500 dark:text-green-400 flex-shrink-0">✓</span>
                <span className="text-gray-900 dark:text-gray-200">Bez ograničenja</span>
              </li>
              <li className="flex items-center sm:items-start justify-center sm:justify-start gap-2 w-full">
                <span className="text-green-500 dark:text-green-400 flex-shrink-0">✓</span>
                <span className="text-gray-900 dark:text-gray-200">24/7 Premium podrška</span>
              </li>
            </ul>
            <button type="button" onClick={() => openAuthModal('register')} className="btn-primary w-full">
              Registruj se
            </button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-12 text-gray-900 dark:text-gray-100">
            {t('landing.faq.title') || 'Često postavljana pitanja'}
          </h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {[
              {
                question: t('landing.faq.q1') || 'Kako funkcioniše Nannity?',
                answer: t('landing.faq.a1') || 'Nannity je platforma koja povezuje roditelje i dadilje. Roditelji mogu da prate sate rada, automatski izračunavaju plaćanja i upravljaju kalendarom. Dadilje mogu da prate svoje poslove, zaradu i primaju notifikacije o novim prilikama.'
              },
              {
                question: t('landing.faq.q2') || 'Da li je platforma besplatna?',
                answer: t('landing.faq.a2') || 'Da, postoji besplatan plan sa osnovnim funkcijama. Takođe imamo Osnovni plan (499 RSD/mesec) i Premium plan (899 RSD/mesec) sa dodatnim funkcijama kao što je AI pametno uparivanje.'
              },
              {
                question: t('landing.faq.q3') || 'Kako funkcioniše automatsko plaćanje?',
                answer: t('landing.faq.a3') || 'Možete podesiti dinamiku plaćanja (mesečno, nedeljno, ili po određenim danima). Platforma automatski izračunava iznos na osnovu sati rada i satnice, a vi dobijate podsetnike pre datuma plaćanja.'
              },
              {
                question: t('landing.faq.q5') || 'Mogu li da podelim kalendar sa porodicom?',
                answer: t('landing.faq.a5') || 'Da! Možete podeliti kalendar, plaćanja i veze sa bebisiterkama sa članovima porodice. Samo izaberite šta želite da podelite kada delite kalendar sa prijateljem.'
              },
              {
                question: t('landing.faq.q6') || 'Kako da počnem?',
                answer: t('landing.faq.a6') || 'Jednostavno se registrujte besplatno, popunite svoj profil i počnite da koristite platformu. Možete odmah da dodate bebisiterku ili da počnete da tražite posao kao dadilja.'
              }
            ].map((faq, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-all"
              >
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                  className="w-full px-6 sm:px-8 py-5 sm:py-6 text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  aria-expanded={openFAQ === index}
                  aria-controls={`faq-answer-${index}`}
                >
                  <span className="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-100 pr-4">
                    {faq.question}
                  </span>
                  {openFAQ === index ? (
                    <MdExpandLess className="text-2xl sm:text-3xl text-primary-600 dark:text-primary-400 flex-shrink-0" />
                  ) : (
                    <MdExpandMore className="text-2xl sm:text-3xl text-primary-600 dark:text-primary-400 flex-shrink-0" />
                  )}
                </button>
                <div
                  id={`faq-answer-${index}`}
                  className={`overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out ${
                    openFAQ === index ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-6 sm:px-8 pt-3 pb-5 sm:pb-6 text-base sm:text-lg text-gray-700 dark:text-gray-300">
                    {faq.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 sm:py-16 lg:py-20 relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100 tracking-tight mb-3">
              Kontakt
            </h2>
            <p className="text-base sm:text-lg text-gray-700 dark:text-gray-300 max-w-2xl mx-auto">
              Imaš pitanje, predlog ili prijavu problema? Pošalji nam poruku i odgovorićemo što pre.
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            <div className="group relative bg-white/70 dark:bg-gray-800/70 backdrop-blur-md rounded-2xl p-6 sm:p-7 border border-white/40 dark:border-slate-700/60 shadow-sm">
              <form onSubmit={submitContact} className="space-y-4">
                {/* Honeypot (bots fill this) */}
                <input
                  type="text"
                  value={contactForm.company}
                  onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })}
                  className="hidden"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      Ime
                    </label>
                    <input
                      type="text"
                      required
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-200 focus:border-primary-300 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                      placeholder="Tvoje ime"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      required
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-200 focus:border-primary-300 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                      placeholder="tvoj@email.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    Naslov (opciono)
                  </label>
                  <input
                    type="text"
                    value={contactForm.subject}
                    onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                    className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-200 focus:border-primary-300 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                    placeholder="Npr. Predlog za aplikaciju"
                    maxLength={120}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    Poruka
                  </label>
                  <textarea
                    required
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-200 focus:border-primary-300 dark:bg-gray-800 dark:border-gray-700 dark:text-white min-h-[140px] resize-y"
                    placeholder="Napiši poruku..."
                    maxLength={2000}
                  />
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 text-right">
                    {contactForm.message.length}/2000
                  </div>
                </div>

                {contactStatus.state !== 'idle' && contactStatus.message && (
                  <div
                    className={`text-sm rounded-xl px-4 py-3 border ${
                      contactStatus.state === 'success'
                        ? 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-200'
                        : contactStatus.state === 'error'
                        ? 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-200'
                        : 'bg-gray-50 border-gray-200 text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200'
                    }`}
                  >
                    {contactStatus.message}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={contactStatus.state === 'sending'}
                  className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {contactStatus.state === 'sending' ? 'Šaljem...' : 'Pošalji poruku'}
                </button>

                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Slanjem poruke pristaješ da te kontaktiramo email-om u vezi ovog upita.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 bg-[var(--lp-surface-soft)] border-t border-[var(--lp-border)]">
        <div className="landing-container text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3 text-[var(--lp-text)]">
            {t('landing.cta.title')}
          </h2>
          <p className="text-base sm:text-lg mb-6 max-w-2xl mx-auto text-[var(--lp-text-muted)]">
            {t('landing.cta.description')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button type="button" onClick={() => openAuthModal('register')} className="landing-btn-cta px-6 py-3">
              Registruj se
            </button>
            <button type="button" onClick={() => openAuthModal('login')} className="landing-btn-ghost px-6 py-3">
              Uloguj se
            </button>
          </div>
        </div>
      </section>
      </div>

      {/* Modal: Registruj se da vidiš detalje oglasa — prikazuje se kad gost klikne na rezultat pretrage */}
      {showRegisterPrompt && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50" onClick={() => setShowRegisterPrompt(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-6 border border-gray-200 dark:border-gray-700" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Registruj se da vidiš sve detalje
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Kreiraj nalog ili se prijavi da bi mogao/la da vidiš pun opis oglasa, kontakt i ostale informacije.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                className="btn-primary text-center flex-1"
                onClick={() => {
                  setShowRegisterPrompt(false);
                  openAuthModal('register', registerPromptTarget);
                }}
              >
                Registruj se
              </button>
              <button
                type="button"
                className="btn-secondary text-center flex-1"
                onClick={() => {
                  setShowRegisterPrompt(false);
                  openAuthModal('login', registerPromptTarget);
                }}
              >
                Prijavi se
              </button>
            </div>
            <button
              type="button"
              className="mt-4 w-full text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              onClick={() => { setShowRegisterPrompt(false); setRegisterPromptTarget(null); }}
            >
              Zatvori
            </button>
          </div>
        </div>
      )}

      <Footer variant="landing" />
      <AuthModal
        isOpen={authModalOpen}
        initialMode={authModalMode}
        redirectTo={registerPromptTarget}
        onClose={() => {
          setAuthModalOpen(false);
          setRegisterPromptTarget(null);
          if (initialAuthMode) navigate('/', { replace: true });
        }}
      />
    </div>
  );
}
